<template>
  <div class="space-y-8">
    <div class="sm:flex sm:items-center sm:justify-between">
      <div>
        <h1 class="text-base/7 font-semibold text-gray-900 dark:text-white">Caches</h1>
        <p class="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
          S3-compatible buckets that GitLab runners use for the GitLab CI <code>cache:</code>
          keyword; GitHub runners cannot use them. A shared cache lets every runner that uses it
          restore what another runner saved.
        </p>
      </div>
      <router-link
        v-if="canManage"
        :to="{ name: 'CreateCache' }"
        class="mt-4 inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:mt-0"
        data-testid="create-cache"
      >
        <PlusIcon class="-ml-0.5 size-5" aria-hidden="true" />
        New cache
      </router-link>
    </div>

    <p v-if="loading" class="text-sm text-gray-500 dark:text-gray-400">Loading caches…</p>
    <p v-else-if="loadError" class="text-sm text-red-600 dark:text-red-400" role="alert">
      {{ loadError }}
    </p>
    <div
      v-else-if="caches.length === 0"
      class="rounded-lg border border-dashed border-gray-300 p-8 text-center dark:border-white/15"
      data-testid="caches-empty"
    >
      <CircleStackIcon class="mx-auto size-10 text-gray-400" aria-hidden="true" />
      <p class="mt-2 text-sm font-semibold text-gray-900 dark:text-white">No caches yet</p>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Add a bucket from Hetzner Object Storage, AWS S3, Cloudflare R2 or any other S3-compatible
        provider, then select it when you create or edit a runner.
      </p>
    </div>
    <ul
      v-else
      role="list"
      class="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 dark:divide-white/10 dark:bg-gray-800/50 dark:ring-white/10"
    >
      <li
        v-for="cache in caches"
        :key="cache.id"
        class="flex flex-col gap-4 px-4 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6"
        data-testid="cache-row"
      >
        <div class="min-w-0 space-y-1">
          <div class="flex flex-wrap items-center gap-2">
            <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ cache.name }}</p>
            <span
              :class="[
                cache.shared
                  ? 'bg-green-50 text-green-700 inset-ring-green-600/20 dark:bg-green-400/10 dark:text-green-400 dark:inset-ring-green-500/20'
                  : 'bg-gray-50 text-gray-600 inset-ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:inset-ring-gray-400/20',
                'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium inset-ring',
              ]"
            >
              {{ cache.shared ? 'Shared' : 'Per runner' }}
            </span>
            <span
              v-if="cache.insecure"
              class="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 inset-ring inset-ring-yellow-600/20 dark:bg-yellow-400/10 dark:text-yellow-500 dark:inset-ring-yellow-400/20"
            >
              HTTP
            </span>
          </div>
          <p class="truncate text-sm text-gray-500 dark:text-gray-400">
            {{ cache.insecure ? 'http' : 'https' }}://{{ cache.serverAddress }}/{{ cache.bucketName
            }}{{ cache.pathPrefix ? `/${cache.pathPrefix}` : '' }} · {{ cache.bucketLocation }}
          </p>
          <div class="text-sm text-gray-500 dark:text-gray-400" data-testid="cache-used-by">
            <template v-if="cache.usedBy.length === 0">Not used by any runner</template>
            <template v-else>
              Used by
              <template v-for="(runner, index) in cache.usedBy" :key="runner.runnerId">
                <span class="font-medium text-gray-700 dark:text-gray-300">{{ runner.name }}</span>
                <span class="text-gray-400"> ({{ stateLabel(runner.state) }})</span
                ><template v-if="index < cache.usedBy.length - 1">, </template>
              </template>
            </template>
          </div>
        </div>
        <div v-if="canManage" class="flex shrink-0 gap-3">
          <router-link
            :to="{ name: 'EditCache', params: { id: cache.id } }"
            class="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20"
            :data-testid="`edit-cache-${cache.id}`"
          >
            Edit
          </router-link>
          <button
            type="button"
            class="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-red-600 shadow-xs inset-ring inset-ring-gray-300 hover:bg-red-50 dark:bg-white/10 dark:text-red-400 dark:inset-ring-white/5 dark:hover:bg-white/20 cursor-pointer"
            :data-testid="`delete-cache-${cache.id}`"
            @click="askDelete(cache)"
          >
            Delete
          </button>
        </div>
      </li>
    </ul>

    <ConfirmDialog
      :open="deleting !== null"
      :title="`Delete ${deleting?.name ?? 'cache'}?`"
      message="The cache configuration is removed from ManageRunners. Objects in the bucket are not deleted."
      confirm-label="Delete"
      busy-label="Deleting…"
      :busy="deleteBusy"
      :error="deleteError"
      @confirm="confirmDelete"
      @cancel="deleting = null"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { CircleStackIcon, PlusIcon } from '@heroicons/vue/24/outline';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import {
  asProblem,
  conflictRunners,
  deleteCache,
  listCaches,
  type RunnerCache,
} from '@/api/caches';
import { useOrganizationStore } from '@/stores/organization';

const organizations = useOrganizationStore();
const canManage = computed(() => organizations.canManageRunners);

const caches = ref<RunnerCache[]>([]);
const loading = ref(true);
const loadError = ref('');

const deleting = ref<RunnerCache | null>(null);
const deleteBusy = ref(false);
const deleteError = ref<string | null>(null);

function stateLabel(state: string) {
  return state.toLowerCase().replace(/_/g, ' ');
}

async function load() {
  loading.value = true;
  loadError.value = '';
  try {
    caches.value = await listCaches();
  } catch (error) {
    const e = asProblem(error);
    loadError.value = e?.response?.data?.detail || e?.message || 'Failed to load caches.';
  } finally {
    loading.value = false;
  }
}

function askDelete(cache: RunnerCache) {
  deleteError.value = null;
  deleting.value = cache;
}

async function confirmDelete() {
  const cache = deleting.value;
  if (!cache) return;
  deleteBusy.value = true;
  deleteError.value = null;
  try {
    await deleteCache(cache.id);
    deleting.value = null;
    await load();
  } catch (error) {
    const e = asProblem(error);
    const runners = conflictRunners(error, 'CACHE_IN_USE');
    deleteError.value = runners
      ? `Remove the cache from these runners first: ${runners.map((r) => r.name).join(', ')}.`
      : e?.response?.data?.detail || e?.message || 'Failed to delete the cache.';
  } finally {
    deleteBusy.value = false;
  }
}

onMounted(load);
</script>
